import { T, useGT } from 'gt-react';
import { PlusIcon, WarningIcon } from '@phosphor-icons/react';
import type { TerminalTaskStatus } from '@erp/shared-types';
import { Button } from 'src/components/ui/button';
import { AlertContainer, AlertDescription } from 'src/components/ui/alert';
import {
  closeActionPreset,
  closeStatusesCoveredByActions,
  type TaskTypeActionDraft,
} from './taskTypeAdmin';

/**
 * Custom actions replace the default Complete / Fail / Cancel controls on the
 * task page. Warns when the drafted actions leave a task with no way to close,
 * and offers one-click actions for each close status not yet covered.
 */
export function TaskTypeActionCloseGuardrails({
  drafts,
  outcomeChoices,
  disabled,
  onAdd,
}: {
  drafts: TaskTypeActionDraft[];
  outcomeChoices: readonly string[];
  disabled?: boolean;
  onAdd: (draft: TaskTypeActionDraft) => void;
}) {
  const gt = useGT();
  const covered = closeStatusesCoveredByActions(drafts);
  const presets: Array<{ status: TerminalTaskStatus; label: string }> = [
    {
      status: 'completed',
      label: gt('Complete', { context: 'default label for a close action' }),
    },
    {
      status: 'failed',
      label: gt('Fail', { context: 'default label for a fail action' }),
    },
    {
      status: 'cancelled',
      label: gt('Cancel', { context: 'default label for a cancel action' }),
    },
  ];
  const missing = presets.filter(({ status }) => !covered.includes(status));

  return (
    <div className="flex flex-col gap-2">
      {drafts.length > 0 && covered.length === 0 ? (
        <AlertContainer variant="destructive">
          <WarningIcon />
          <AlertDescription className="text-xs">
            <T context="warning when no custom task action closes the task">
              None of these actions closes the task. Tasks of this type hide the
              default Complete, Fail and Cancel buttons, so people will have no
              way to close them from the task page. Add an action that records
              an outcome.
            </T>
          </AlertDescription>
        </AlertContainer>
      ) : null}
      {drafts.length > 0 && covered.length > 0 && missing.length > 0 ? (
        <p className="text-[11px] text-muted-foreground">
          <T context="hint when custom task actions cover only some close statuses">
            People can only close tasks of this type with the statuses your
            actions record. Add the missing ones below if they apply.
          </T>
        </p>
      ) : null}
      {missing.length > 0 ? (
        <div
          className="flex flex-wrap items-center gap-1.5"
          data-testid="task-type-close-action-presets"
        >
          <span className="text-[11px] text-muted-foreground">
            <T context="label before buttons adding standard close actions">
              Add a close action:
            </T>
          </span>
          {missing.map(({ status, label }) => (
            <Button
              key={status}
              type="button"
              variant="outline"
              size="sm"
              disabled={disabled}
              onClick={() =>
                onAdd(
                  closeActionPreset({
                    status,
                    label,
                    reasonLabel: gt('Reason', {
                      context: 'form field asking why a task is cancelled',
                    }),
                    outcomeChoices,
                    existing: drafts,
                  }),
                )
              }
            >
              <PlusIcon className="h-4 w-4" />
              {label}
            </Button>
          ))}
        </div>
      ) : null}
    </div>
  );
}
